'use client'
import { memo } from 'react'
import styled from 'styled-components'
import { mq, vw, getP53, font18_22, font20_24, font30_42, font15_20, font26_24, defaultGrid } from '@/styles'

type ContentTypeText = {
  col: { from: number, expand: number},
  row: { from: number, expand: number},
  align?: {
    v: 'top' | 'center' | 'bottom',
    h: 'left' | 'center' | 'right'
  },
  color?: string,
  text: string,
  type: 'text'
}

interface IContentText {
  data: ContentTypeText
}

export const ContentText = memo(({ data }:IContentText) => {
  const { text, color, align } = data

  return (
    <TextWrapper className={'text-wrapper'} $color={color} $align={align} dangerouslySetInnerHTML={{ __html: text }} />
  )
})

const TextWrapper = styled.div<any>`
  font-family: var(--font-basier-circle);
  color: var(--color-red);
  text-align: ${({ $align }) => $align && $align.h ? $align.h : 'left'};
  word-break: break-word;
  width: 100%;

  h2, h3, h4,
  p, ul, ol, dl,
  q, a {
    color: inherit;
    text-align: inherit;
    word-break: inherit;
  }

  p, ul, ol, dl, q, a {
    color: var(--color-red);
  }

  h2, h3, h4, h5, h6 {
    color: var(--color-red);
  }

  h2, h3, h4 {
    font-weight: 500;
    margin-bottom: ${vw(16, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(16, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 20px;
    }

    + p {
      margin-top: -${vw(10, 'mobile')};

      ${mq.greaterThan('tablet')} {
        margin-top: -${vw(10, 'desktop')};
      }
    }
  }

  h1, h2 {
    ${font30_42(true)}

    ${mq.greaterThan('tablet')} {
      ${font30_42(false)}
    }

    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(15, 'mobile')};

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(20, 'desktop')};
        }
      }
    }
  }

  h3 {
    ${font26_24(true)}

    ${mq.greaterThan('tablet')} {
      ${font26_24(true)}
    }

    ${mq.greaterThan('tablet')} {
      ${font26_24(false)}
    }

    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(15, 'mobile')};

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(15, 'desktop')};
        }
      }
    }
  }

  h4 {
    ${font18_22(true)}

    ${mq.greaterThan('tablet')} {
      ${font18_22(false)}
    }
  }

  h5 {
    ${font15_20(true)}

    ${mq.greaterThan('tablet')} {
      ${font15_20(false)}
    }
  }

  h4, h5 {
    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(10, 'mobile')};

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(10, 'desktop')};
        }
      }
    }
  }

  h6 {
    ${font15_20(true)}

    ${mq.greaterThan('tablet')} {
      ${font15_20(false)}
    }

    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(8, 'mobile')};

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(8, 'desktop')};
        }
      }
    }
  }

  p {
    ${font26_24(true)}
    font-family: var(--font-basier-circle);

    ${mq.greaterThan('tablet')} {
      ${font26_24(false)}
    }

    br {
      content: '';
      display: block;
      margin-bottom:  ${vw(10, 'mobile')};

      ${mq.greaterThan('tablet')} {
        margin-bottom: ${vw(15, 'desktop')};
      }
    }
  }

  ul, ol, dl {
    ${font20_24(true)}
    font-weight: normal;
    margin-bottom: ${vw(48, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(48, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      ${font20_24(false)}
      margin-bottom: ${vw(60, 'desktop')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: 60px;
    }

    &:last-child {
      margin-bottom: 0;
    }

    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(10, 'mobile')};

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(15, 'desktop')};
        }
      }
    }
  }




  br {
    + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
      ${font20_24(true)}

      ${mq.greaterThan('tablet')} {
        ${font20_24(false)}
      }
    }
  }

  ul, ol {
    padding-left: 18px;
  }

  ul {
    li {
      list-style-type: disc;
    }
  }

  ol {
    li {
      list-style-type: decimal;
    }
  }

  q, blockquote {
    border-left: 0;
    color: var(--color-red);
    ${getP53()}
    display: block;
    font-weight: bold;
    font-style: normal;
    margin: auto;
    padding: ${vw(12, 'mobile')} ${vw(20, 'mobile')} 0;
    position: relative;
    text-align: center;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      padding: ${vw(20, 'desktop')} 0 ${vw(20, 'desktop')};
      transform: translateX(-${vw(defaultGrid.mobile.columns.width, 'desktop')});
      width: calc(100% + ${vw(defaultGrid.mobile.columns.width * 2, 'desktop')});
    }

    &:before,
    &:after {
      content: '';
      background-image: url('/images/svg/i--double-quotes.svg');
      background-repeat: no-repeat;
      display: inline-block;
      height: ${vw(33, 'mobile')};
      opacity: .2;
      position: absolute;
      user-select: none;
      width: ${vw(47, 'mobile')};

      ${mq.greaterThan('tablet')} {
        height: ${vw(66, 'desktop')};
        width: ${vw(94, 'desktop')};
      }
    }

    &:before {
      left: ${vw(20, 'mobile')};
      top: -${vw(10, 'mobile')};
      transform: rotate(180deg);

      ${mq.greaterThan('tablet')} {
        left: ${vw(30, 'desktop')};
        top: -${vw(10, 'desktop')};
      }
    }

    &:after {
      right: ${vw(20, 'mobile')};
      bottom: -${vw(10, 'mobile')};

      ${mq.greaterThan('tablet')} {
        bottom: 0;
        right: ${vw(30, 'desktop')};
      }
    }

    > p {
      color: var(--color-red);
      display: inline;
      ${getP53()}
      font-family: var(--font-basier-circle);
      font-style: normal;
      text-align: center;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;

    th,
    td {
      ${font20_24(true)}
      padding: ${vw(20, 'mobile')} ${vw(5, 'mobile')};

      ${mq.greaterThan('tablet')} {
        ${font20_24(false)}
        padding: ${vw(30.5, 'desktop')}vw ${vw(10, 'desktop')};
      }

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }
    }

    th {
      color: inherit;
      border-bottom: 1px solid inherit;
    }

    tbody, tfoot {
      td {
        border-bottom: 1px solid inherit;
        opacity: .6;

        ${mq.greaterThan('tablet')} {
          max-width: ${vw(490, 'desktop')};
          vertical-align: initial;
        }
      }

      tr {
        &:last-child {
          td {
            border-color: rgba(0, 0, 0, 0);
          }
        }
      }
    }
  }
`
